import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loadSeedInventory,
  buildBoxInventory,
  archiveCsvAsLegacy,
  findPrefillMatch,
  applyPrefill,
  isLegacyOlderThan,
  summarize,
  type BoxScanEntry,
  type InventoryRecord,
  type InventoryStats,
} from "@/lib/inventory";
import { inProductionMode, getAppMode, displayName } from "@/lib/demoMode";
import { useAuth } from "@/components/AuthProvider";
import { apiRequest } from "@/lib/queryClient";

// Shape of POST /api/dex/manual-entry response.
interface ManualEntryResponse {
  ok: boolean;
  recordId: number | null;
  deduplicated: boolean;
  message?: string;
}

// J2 persistence: write a single user-entered record to D1 so it survives
// logout. Returns the server response. Throws on network / 4xx / 5xx so the
// caller can show a toast and avoid the optimistic-then-rollback trap.
export async function persistManualEntry(rec: InventoryRecord): Promise<ManualEntryResponse> {
  const sourceTag: "manual" | "quick_add" | "two_snap" =
    rec.source === "manual-quick"
      ? "quick_add"
      : rec.source === "manual-deep"
      ? "two_snap"
      : "manual";

  const payload = {
    speciesId: rec.dexNumber,
    formId: rec.form && rec.form !== "Normal" ? rec.form : null,
    level: rec.levelMin || rec.levelMax || null,
    cp: rec.cp || null,
    hp: rec.hp || null,
    ivAtk: typeof rec.ivAtk === "number" ? rec.ivAtk : null,
    ivDef: typeof rec.ivDef === "number" ? rec.ivDef : null,
    ivSta: typeof rec.ivSta === "number" ? rec.ivSta : null,
    isShiny: !!rec.isShiny,
    isLucky: !!rec.isLucky,
    isShadow: !!rec.isShadow,
    isPurified: !!rec.isPurified,
    source: sourceTag,
  };
  const res = await apiRequest("POST", "/api/dex/manual-entry", payload);
  return (await res.json()) as ManualEntryResponse;
}

// Shape returned by GET /api/trainer/profile. Numeric fields are nullable
// because the OCR may not have read every line; the hydration code defaults
// them to 0 / empty string so the TrainerProfile state stays well-typed.
interface TrainerProfileApiRow {
  trainerName?: string | null;
  level?: number | null;
  totalXp?: number | null;
  totalKm?: number | null;
  pokemonCaught?: number | null;
  pokestopsVisited?: number | null;
  startDate?: string | null;
}
interface TrainerProfileResponse {
  profile: TrainerProfileApiRow | null;
  account?: { friendCode?: string | null } | null;
}

// v8, Clean Slate Model.
// Box records and legacy CSV records are kept in TWO SEPARATE LISTS.
// They never mix in stats or in the main inventory UI.
interface InventoryState {
  // Canonical inventory, what the user actually owns right now.
  records: InventoryRecord[];                 // alias for boxRecords (compat)
  boxRecords: InventoryRecord[];
  setRecords: (r: InventoryRecord[]) => void; // sets boxRecords (compat)
  addRecords: (r: InventoryRecord[]) => void; // adds to boxRecords (compat). Manual records also persist to D1.
  addManualRecord: (r: InventoryRecord) => Promise<ManualEntryResponse>; // persisted single-record add; throws on failure
  upsertRecord: (r: InventoryRecord) => void; // upserts in boxRecords

  // Legacy PokéGenie CSV archive, view-only, never mixed with box.
  legacyCsvRecords: InventoryRecord[];
  archiveLegacyOlderThan: (days: number) => number; // returns count archived; in v8 we already archive everything, so this hides anything older
  legacyHidden: Set<string>; // ids hidden by archiveLegacyOlderThan

  // Pre-fill helpers used by the deep-capture flow.
  findPrefillFor: (target: { species: string; cp: number; isLucky?: boolean; isShadow?: boolean; isPurified?: boolean }) => InventoryRecord | null;
  applyPrefillToBox: (boxId: string, legacy: InventoryRecord) => void;

  // Bulk actions exposed on the Inventory hub.
  bulkConfirmBoxScan: () => number; // returns count confirmed

  // Headline stats.
  stats: InventoryStats;

  // Trainer profile (imported from screenshot).
  trainerImported: boolean;
  setTrainerImported: (b: boolean) => void;
  trainer: TrainerProfile;
  setTrainer: (t: Partial<TrainerProfile>) => void;

  // Box scan state.
  boxScanCompleted: boolean;
  setBoxScanCompleted: (b: boolean) => void;
}

export interface TrainerProfile {
  name: string;
  level: number;
  totalXP: number;
  pokemonCaught: number;
  distanceKm: number;
  pokestopsVisited: number;
  startDate: string;
  friendCode: string; // 12 digits, formatted as "1234 5678 9012"
}

// Demo/screenshot-mode seed data. Built once at module load and only
// outside production. The conditional literal (import.meta.env.PROD ?
// {} : { ...real data... }) is the key tree-shake hook: Vite + terser
// in production mode eliminate the dead branch entirely so the seed
// constants never appear in dist/public bundles.
const EMPTY_TRAINER: TrainerProfile = {
  name: "", level: 0, totalXP: 0, pokemonCaught: 0, distanceKm: 0,
  pokestopsVisited: 0, startDate: "", friendCode: "",
};

const DEFAULT_TRAINER: TrainerProfile = import.meta.env.PROD
  ? EMPTY_TRAINER
  : {
      name: "InfernalVamp",
      level: 77,
      totalXP: 210774127,
      pokemonCaught: 154338,
      distanceKm: 10278.9,
      pokestopsVisited: 133357,
      startDate: "7/07/2016",
      friendCode: "1234 5678 4321",
    };

// Box-scan tiles drawn from the prototype's reference screenshots. Real
// Pokémon at real CPs, kept out of the production bundle via the
// import.meta.env.PROD ternary below.
const BOX_SCAN_DATE = "2026-04-25T00:00:00Z";
const VISIBLE_BOX_TILES: BoxScanEntry[] = import.meta.env.PROD ? [] : [
  // IMG_9279, top of box
  { species: "Zamazenta", cp: 3720, isLucky: true, isShiny: true, isHundo: true, dexNumber: 889, scanDate: BOX_SCAN_DATE },
  { species: "Annihilape", cp: 3681, isLucky: true, dexNumber: 979, scanDate: BOX_SCAN_DATE },
  { species: "Landorus", cp: 3674, isLucky: true, dexNumber: 645, scanDate: BOX_SCAN_DATE },
  { species: "Gholdengo", cp: 3644, isLucky: true, dexNumber: 1000, scanDate: BOX_SCAN_DATE },
  { species: "Snorlax", cp: 3629, dexNumber: 143, scanDate: BOX_SCAN_DATE },
  { species: "Magnezone", cp: 3605, isLucky: true, dexNumber: 462, scanDate: BOX_SCAN_DATE },
  { species: "Ceruledge", cp: 3572, isShadow: true, dexNumber: 936, scanDate: BOX_SCAN_DATE },
  { species: "Gigalith", cp: 3569, isShadow: true, dexNumber: 526, scanDate: BOX_SCAN_DATE },
  { species: "Giratina", cp: 3565, dexNumber: 487, scanDate: BOX_SCAN_DATE },
  { species: "Primarina", cp: 3554, isLucky: true, isHundo: true, dexNumber: 730, scanDate: BOX_SCAN_DATE },
  { species: "Ursaluna", cp: 3524, isHundo: true, dexNumber: 901, scanDate: BOX_SCAN_DATE },
  { species: "Gallade", cp: 3449, isLucky: true, dexNumber: 475, scanDate: BOX_SCAN_DATE },
  // IMG_9280, scrolled down, ~CP 2920 region
  { species: "Kyogre", cp: 2921, isShadow: true, isShiny: true, dexNumber: 382, scanDate: BOX_SCAN_DATE },
  { species: "Espeon", cp: 2920, dexNumber: 196, scanDate: BOX_SCAN_DATE },
  { species: "Palkia", cp: 2920, dexNumber: 484, scanDate: BOX_SCAN_DATE },
  { species: "Kyogre", cp: 2913, isShadow: true, dexNumber: 382, scanDate: BOX_SCAN_DATE },
  { species: "Palkia", cp: 2912, dexNumber: 484, scanDate: BOX_SCAN_DATE },
  { species: "Palkia", cp: 2910, dexNumber: 484, scanDate: BOX_SCAN_DATE },
  { species: "Groudon", cp: 2910, dexNumber: 383, scanDate: BOX_SCAN_DATE },
  { species: "Rampardos", cp: 2908, isShadow: true, dexNumber: 409, scanDate: BOX_SCAN_DATE },
  { species: "Palkia", cp: 2903, dexNumber: 484, scanDate: BOX_SCAN_DATE },
  { species: "Groudon", cp: 2902, dexNumber: 383, scanDate: BOX_SCAN_DATE },
  { species: "Groudon", cp: 2901, dexNumber: 383, scanDate: BOX_SCAN_DATE },
  { species: "Melmetal", cp: 2900, dexNumber: 809, scanDate: BOX_SCAN_DATE },
  { species: "Palkia", cp: 2899, isShadow: true, isShiny: true, dexNumber: 484, scanDate: BOX_SCAN_DATE },
];

// Seed builder.
//
// In demo or screenshot mode (the prototype build) we pre-populate the box
// from VISIBLE_BOX_TILES and the legacy archive from the bundled CSV so
// designers and reviewers see a populated app.
//
// In production mode (App Store build) we ship an EMPTY inventory. The user
// populates it through Box Scan (their own screenshots) or by opting into the
// PokéGenie CSV import. This satisfies audit R5: "All species names in
// production come from user input or from a data file the user can opt
// into, never from hardcoded marketing copy."
function applyScreenshotMode(records: InventoryRecord[]): InventoryRecord[] {
  // In screenshot mode every record's species name is replaced with a generic
  // "Specimen NNNN" label so the App Store assets reveal no real names.
  if (getAppMode() !== "screenshot") return records;
  return records.map((r) => ({ ...r, species: displayName(r.species, r.dexNumber) }));
}

function buildSeed(): { boxRecords: InventoryRecord[]; legacyCsvRecords: InventoryRecord[]; totalBoxSize: number } {
  if (inProductionMode()) {
    return { boxRecords: [], legacyCsvRecords: [], totalBoxSize: 0 };
  }
  const csvRecords = loadSeedInventory();
  return {
    boxRecords: applyScreenshotMode(buildBoxInventory(VISIBLE_BOX_TILES)),
    legacyCsvRecords: applyScreenshotMode(archiveCsvAsLegacy(csvRecords)),
    totalBoxSize: 11064,
  };
}

const InventoryContext = createContext<InventoryState | null>(null);

export function InventoryProvider({ children }: { children: ReactNode }) {
  const [seed] = useState(() => buildSeed());
  const [boxRecords, setBoxRecords] = useState<InventoryRecord[]>(() => seed.boxRecords);
  const [legacyCsvRecords] = useState<InventoryRecord[]>(() => seed.legacyCsvRecords);
  const [legacyHidden, setLegacyHidden] = useState<Set<string>>(() => new Set());
  // In production mode the user starts fresh and walks through onboarding.
  // In demo / screenshot mode the prototype shows a populated example.
  const startEmpty = inProductionMode();
  const [trainerImported, setTrainerImported] = useState(!startEmpty);
  const [trainer, setTrainerState] = useState<TrainerProfile>(
    startEmpty
      ? { name: "", level: 0, totalXP: 0, pokemonCaught: 0, distanceKm: 0, pokestopsVisited: 0, startDate: "", friendCode: "" }
      : DEFAULT_TRAINER
  );
  const [boxScanCompleted, setBoxScanCompleted] = useState(!startEmpty);

  const visibleLegacy = useMemo(
    () => legacyCsvRecords.filter((r) => !legacyHidden.has(r.id)),
    [legacyCsvRecords, legacyHidden],
  );

  const stats = useMemo(
    () => summarize(boxRecords, visibleLegacy, seed.totalBoxSize),
    [boxRecords, visibleLegacy, seed.totalBoxSize],
  );

  const qc = useQueryClient();

  // Bulk add. Used by CSV import (large batches that go through their own
  // ingest endpoints) and by manual flows. Manual records (source =
  // manual-deep | manual-quick) are persisted to D1 in the background so
  // they survive logout. Network failures during persistence are logged but
  // do NOT roll back local state here, callers needing strict guarantees
  // should use addManualRecord directly.
  function addRecords(rs: InventoryRecord[]) {
    setBoxRecords((prev) => [...rs, ...prev]);
    for (const r of rs) {
      if (r.source === "manual-deep" || r.source === "manual-quick") {
        persistManualEntry(r)
          .then(() => {
            qc.invalidateQueries({ queryKey: ["/api/dex/batches"] });
            qc.invalidateQueries({ queryKey: ["/api/dex/records"] });
            qc.invalidateQueries({ queryKey: ["/api/user/state"] });
          })
          .catch((err) => {
            console.warn("[manual-entry] background persist failed", err);
          });
      }
    }
  }

  // Persistence-first add for a single manual record. Throws on network /
  // 4xx / 5xx so the caller can show a toast and skip updating local state.
  // After a successful write, invalidates the dex caches so the new record
  // is visible the next time the user navigates back.
  async function addManualRecord(r: InventoryRecord): Promise<ManualEntryResponse> {
    const resp = await persistManualEntry(r);
    setBoxRecords((prev) => [r, ...prev]);
    qc.invalidateQueries({ queryKey: ["/api/dex/batches"] });
    qc.invalidateQueries({ queryKey: ["/api/dex/records"] });
    qc.invalidateQueries({ queryKey: ["/api/user/state"] });
    return resp;
  }
  function upsertRecord(r: InventoryRecord) {
    setBoxRecords((prev) => {
      const i = prev.findIndex((p) => p.id === r.id);
      if (i === -1) return [r, ...prev];
      const next = prev.slice();
      next[i] = r;
      return next;
    });
  }
  function findPrefillFor(target: { species: string; cp: number; isLucky?: boolean; isShadow?: boolean; isPurified?: boolean }) {
    return findPrefillMatch(target, visibleLegacy);
  }
  function applyPrefillToBox(boxId: string, legacy: InventoryRecord) {
    setBoxRecords((prev) => prev.map((r) => (r.id === boxId ? applyPrefill(r, legacy) : r)));
  }
  function bulkConfirmBoxScan() {
    let count = 0;
    setBoxRecords((prev) =>
      prev.map((r) => {
        if (r.reconciliationStatus !== "confirmed") {
          count++;
          return { ...r, reconciliationStatus: "confirmed" as const, lastSeenInBox: BOX_SCAN_DATE };
        }
        return r;
      }),
    );
    return count;
  }
  function archiveLegacyOlderThan(days: number) {
    const now = Date.now();
    let count = 0;
    setLegacyHidden((prev) => {
      const next = new Set(prev);
      for (const r of legacyCsvRecords) {
        if (next.has(r.id)) continue;
        if (isLegacyOlderThan(r, days, now)) {
          next.add(r.id);
          count++;
        }
      }
      return next;
    });
    return count;
  }
  function setTrainer(t: Partial<TrainerProfile>) {
    setTrainerState((prev) => ({ ...prev, ...t }));
  }

  // Hydrate trainer state from the server once we know who's logged in. The
  // POST /api/auth/portal/profile/confirm round-trip writes to D1, and this
  // GET lets the UI come back to a populated trainer card after refresh.
  // Gated on auth so we don't fire 401s for signed-out visitors.
  const { account: authedAccount } = useAuth();
  const profileQ = useQuery<TrainerProfileResponse>({
    queryKey: ["/api/trainer/profile"],
    enabled: Boolean(authedAccount?.id),
    staleTime: 60_000,
  });
  useEffect(() => {
    const row = profileQ.data?.profile;
    const acc = profileQ.data?.account;
    if (!row && !acc) return;
    const accountFc = acc?.friendCode ?? "";
    setTrainerState((prev) => ({
      ...prev,
      name: row?.trainerName ?? (acc as any)?.trainerName ?? prev.name,
      level: row?.level ?? prev.level,
      totalXP: row?.totalXp ?? prev.totalXP,
      pokemonCaught: row?.pokemonCaught ?? prev.pokemonCaught,
      distanceKm: row?.totalKm ?? prev.distanceKm,
      pokestopsVisited: row?.pokestopsVisited ?? prev.pokestopsVisited,
      startDate: row?.startDate ?? prev.startDate,
      friendCode: accountFc
        ? accountFc.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ")
        : prev.friendCode,
    }));
    if (row) setTrainerImported(true);
  }, [profileQ.data]);

  // Account-level hydration. /api/auth/me carries the canonical handle, friend
  // code, and trainer level that the Settings edit flow writes via PATCH
  // /api/auth/me. Mirror those into the InventoryProvider trainer so screens
  // that pre-date the patchMe API (Profile hero, Friends row, etc.) keep
  // working off the same source.
  useEffect(() => {
    if (!authedAccount) return;
    setTrainerState((prev) => {
      const next = { ...prev };
      const accName = (authedAccount as any).trainerName as string | null | undefined;
      const accFc = (authedAccount as any).friendCode as string | null | undefined;
      const accLevel = (authedAccount as any).trainerLevel as number | null | undefined;
      if (accName && !prev.name) next.name = accName;
      if (accName && prev.name !== accName) next.name = accName;
      if (typeof accLevel === "number" && accLevel > 0) next.level = accLevel;
      if (accFc) {
        const formatted = accFc.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ");
        if (prev.friendCode !== formatted) next.friendCode = formatted;
      }
      return next;
    });
  }, [authedAccount?.trainerName, authedAccount?.friendCode, authedAccount?.trainerLevel]);

  // PR-A: hydrate the dex headline counts from /api/settings/all on sign-in.
  // The trainer-profile query above covers the trainer card; /settings/all
  // returns dex stats (total / shinies / lucky / hundos) and lets the user
  // pick up exactly where they left off without re-uploading anything.
  const settingsQ = useQuery<{
    settings: unknown;
    trainer: unknown;
    dex: { total: number; shinies: number; lucky: number; hundos: number };
  }>({
    queryKey: ["/api/settings/all"],
    enabled: Boolean(authedAccount?.id),
    staleTime: 60_000,
  });
  useEffect(() => {
    const dex = settingsQ.data?.dex;
    if (!dex) return;
    setTrainerState((prev) => ({
      ...prev,
      pokemonCaught: prev.pokemonCaught || dex.total,
    }));
  }, [settingsQ.data]);

  return (
    <InventoryContext.Provider
      value={{
        records: boxRecords,
        boxRecords,
        setRecords: setBoxRecords,
        addRecords,
        addManualRecord,
        upsertRecord,
        legacyCsvRecords: visibleLegacy,
        archiveLegacyOlderThan,
        legacyHidden,
        findPrefillFor,
        applyPrefillToBox,
        bulkConfirmBoxScan,
        stats,
        trainerImported,
        setTrainerImported,
        trainer,
        setTrainer,
        boxScanCompleted,
        setBoxScanCompleted,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error("useInventory must be used inside InventoryProvider");
  return ctx;
}

// ── Friend code utility ─────────────────────────────────────────────────
export function formatFriendCode(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 12);
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 4) groups.push(digits.slice(i, i + 4));
  return groups.join(" ");
}
